import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import * as Routes from '@/modules/routes';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import { fromCalendarDate } from '@/components/admin/SysadminSPA/shared/calendarDate';
import AlertMessageActions from './AlertMessageActions';
import type { AlertMessage } from './useContentAlertMessages';

const columnHelper = createColumnHelper<AlertMessage>();

const linkClass = 'tw-text-spa-accent-text hover:tw-text-spa-accent-text-hover tw-no-underline tw-transition-colors';

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

const formatEndDate = (iso: string | null) => (iso ? fromCalendarDate(iso).toLocaleDateString('en-US') : '');

const Muted = ({ children }: { children: React.ReactNode }) => (
  <span className="tw-text-spa-tertiary">{children}</span>
);

// Same globe button as the lessons page's StateViewCell; opens the routed states modal.
const StatesCell = ({ alertMessage }: { alertMessage: AlertMessage }) => {
  const navigate = useNavigate();

  return (
    <SPAButton
      aria-label={`Edit state-specific access for ${alertMessage.title}`}
      className="tw-whitespace-nowrap"
      size="xs"
      onClick={() => navigate(`${alertMessage.id}/states`)}
    >
      <i aria-hidden="true" className="fa fa-globe" />
      {alertMessage.statesSelected}
    </SPAButton>
  );
};

// AIDEV-NOTE: Mirrors the legacy admin/alert_messages index columns; Published is added because the
// legacy page only exposed it through the Publish/Unpublish action label.
const getAlertMessageColumns = (): ColumnDef<AlertMessage, unknown>[] => [
  columnHelper.accessor('title', {
    cell: info => (
      <a className={linkClass} href={Routes.edit_admin_alert_message_path(info.row.original.id)}>
        {info.getValue()}
      </a>
    ),
    header: 'Title',
    size: 200,
  }),
  columnHelper.accessor('typeLabel', {
    header: 'Type',
    size: 90,
  }),
  columnHelper.accessor('category', {
    cell: info => info.getValue() || <Muted>N/A</Muted>,
    header: 'Category',
    size: 90,
  }),
  columnHelper.accessor('appliesTo', {
    header: 'Programs / Lessons',
    size: 200,
  }),
  columnHelper.accessor('location', {
    header: 'Location',
    size: 140,
  }),
  columnHelper.accessor('onPageLoad', {
    cell: info => yesNo(info.getValue()),
    header: 'Page Load',
    size: 80,
  }),
  columnHelper.accessor('statesSelected', {
    cell: info => <StatesCell alertMessage={info.row.original} />,
    header: 'States',
    size: 110,
  }),
  columnHelper.accessor('purpose', {
    cell: info => info.getValue() || <Muted>No note</Muted>,
    header: 'Purpose',
    size: 160,
  }),
  columnHelper.accessor('endDate', {
    cell: info => formatEndDate(info.getValue()),
    header: 'End Date',
    size: 100,
  }),
  columnHelper.accessor('published', {
    cell: info => yesNo(info.getValue()),
    header: 'Published',
    size: 80,
  }),
  columnHelper.display({
    cell: info => <AlertMessageActions alertMessage={info.row.original} />,
    enableSorting: false,
    header: 'Actions',
    id: 'actions',
    size: 110,
  }),
];

export default getAlertMessageColumns;
